import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { type Dispatch, type UnknownAction } from "redux";
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { PackageMinus, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { listDepositDeletionReasons, listDepositDeletionReasonsReset } from "../../actions";
import { SortMenu } from "@/components/SortMenu";
import { DataTable } from "@/components/ui/data-table";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import { DataTablePagination } from "@/components/ui/table-pagination";
import { displayErrorMessage } from "@/utils";
import { getCurrentLanguageLabel } from "@/utils/i18n";

import { createColumns, DEPOSIT_DELETE_REASON_SORT_OPTIONS } from "./columns";
import { type DepositDeleteReason } from "./types";

const PAGE_SIZE = 20;

interface RootState {
  listDepositDeletionReasons: {
    isFetching: boolean;
    errorMessage: string;
    data: { results: DepositDeleteReason[] };
  };
}

interface StateProps {
  errorMessage: string;
  depositDeleteReasons: DepositDeleteReason[];
  isFetching: boolean;
}

interface DispatchProps {
  listDepositDeleteReasons: () => void;
  listDepositDeleteReasonsReset: () => void;
}

type DepositDeleteReasonsProps = StateProps & DispatchProps;

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = <TResult,>(dispatch: AppDispatch, action: unknown): TResult =>
  dispatch(action as UnknownAction) as TResult;

const normalize = (value: string) => value.trim().toLocaleLowerCase();

export const DepositDeleteReasons = ({
  errorMessage,
  depositDeleteReasons,
  isFetching,
  listDepositDeleteReasons,
  listDepositDeleteReasonsReset,
}: DepositDeleteReasonsProps) => {
  const { i18n, t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });

  useEffect(() => {
    listDepositDeleteReasons();

    return () => {
      listDepositDeleteReasonsReset();
    };
  }, [listDepositDeleteReasons, listDepositDeleteReasonsReset]);

  const selectedSort = sorting[0];
  const selectedSortColumn = selectedSort?.id ?? "";
  const selectedSortDirection = selectedSort?.desc ? "desc" : "asc";

  const onSortColumnChange = (sortColumn: string) => {
    setSorting([{ id: sortColumn, desc: selectedSortDirection === "desc" }]);
  };

  const onSortDirectionChange = (sortDirection: string) => {
    setSorting([
      {
        id: selectedSortColumn || DEPOSIT_DELETE_REASON_SORT_OPTIONS[0].id,
        desc: sortDirection === "desc",
      },
    ]);
  };

  const languageLabel = useMemo(
    () => getCurrentLanguageLabel(i18n.language) as string,
    [i18n.language]
  );

  const columns = useMemo(
    () =>
      createColumns(t, {
        languageCode: i18n.language,
        languageLabel,
      }),
    [i18n.language, languageLabel, t]
  );

  const filteredDepositDeleteReasons = useMemo(() => {
    const query = normalize(searchTerm);

    if (!query) {
      return depositDeleteReasons;
    }

    return depositDeleteReasons.filter(reason =>
      [reason.text, reason.translations?.[i18n.language]].some(value =>
        normalize(String(value ?? "")).includes(query)
      )
    );
  }, [depositDeleteReasons, searchTerm, i18n.language]);

  useEffect(() => {
    setPagination(current => (current.pageIndex === 0 ? current : { ...current, pageIndex: 0 }));
  }, [sorting, searchTerm]);

  useEffect(() => {
    setPagination(current => {
      const pageCount = Math.max(
        1,
        Math.ceil(filteredDepositDeleteReasons.length / current.pageSize)
      );
      const nextPageIndex = Math.min(current.pageIndex, pageCount - 1);

      return nextPageIndex === current.pageIndex
        ? current
        : { ...current, pageIndex: nextPageIndex };
    });
  }, [filteredDepositDeleteReasons.length]);

  const table = useReactTable({
    columns,
    data: filteredDepositDeleteReasons,
    enableMultiSort: false,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: row => String(row.id),
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    state: { pagination, sorting },
  });

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-x-2">
          <span className="inline-flex shrink-0 items-center justify-center size-10 rounded-lg p-2 bg-blue-100 text-blue-600">
            <PackageMinus />
          </span>
          <h1 className="font-semibold text-2xl">{t("depositDeleteReasons.title")}</h1>
        </div>
        <p className="text-sm text-muted-foreground">{t("depositDeleteReasons.description")}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <InputGroup className="w-full max-w-xs sm:w-80">
          <InputGroupAddon>
            <Search className="size-4" aria-hidden="true" />
          </InputGroupAddon>
          <InputGroupInput
            id="deposit-delete-reason-input"
            type="search"
            value={searchTerm}
            className="[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
            placeholder={t("depositDeleteReasons.searchPlaceholder")}
            aria-label={t("depositDeleteReasons.searchPlaceholder")}
            onChange={event => setSearchTerm(event.target.value)}
          />
          {searchTerm && (
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label={t("depositDeleteReasons.clearFilter")}
                onClick={() => setSearchTerm("")}
              >
                <X className="size-3.5" aria-hidden="true" />
              </InputGroupButton>
            </InputGroupAddon>
          )}
        </InputGroup>
        <SortMenu
          options={DEPOSIT_DELETE_REASON_SORT_OPTIONS}
          sortColumn={selectedSortColumn}
          sortDirection={selectedSortDirection}
          onSortColumnChange={onSortColumnChange}
          onSortDirectionChange={onSortDirectionChange}
        />
      </div>

      {errorMessage ? (
        displayErrorMessage(errorMessage)
      ) : (
        <div className="overflow-hidden rounded-md border border-border bg-background">
          <DataTable
            emptyMessage={t("depositDeleteReasons.empty")}
            isLoading={isFetching}
            table={table}
          />
          <DataTablePagination table={table} />
        </div>
      )}
    </main>
  );
};

export const mapStateToProps = (state: RootState): StateProps => ({
  errorMessage: state.listDepositDeletionReasons.errorMessage,
  depositDeleteReasons: state.listDepositDeletionReasons.data.results,
  isFetching: state.listDepositDeletionReasons.isFetching,
});

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => ({
  listDepositDeleteReasons: () => dispatchAction(dispatch, listDepositDeletionReasons()),
  listDepositDeleteReasonsReset: () => dispatchAction(dispatch, listDepositDeletionReasonsReset()),
});

export const ConnectedDepositDeleteReasons = connect(
  mapStateToProps,
  mapDispatchToProps
)(DepositDeleteReasons);
